import { useEffect, useMemo, useState } from "react";
import Modal from "../../../layouts/Modal";
import { ButtonInput } from "../Inputs/ButtonInput";
import { IQuote, Status } from "../../../models/Quote";
import { QuoteAcceptedAs, QuotePriceSelection } from "../../../data/useConvertQuoteToLead";
import CustomerPickerPill from "../../customer/CustomerPickerPill";
import { PencilSquareIcon } from "@heroicons/react/24/outline";
import AmendPriceModal from "./AmendPriceModal";
import { InfoBox, InfoRow } from "../InfoBox";

const isUsablePrice = (n?: number | null) => n != null && Number.isFinite(n) && n > 0;
const formatGBP = (n?: number | null) => (n != null && isUsablePrice(n) ? `£${n.toFixed(2)}` : "—");

const PriceOptionRow = ({
  label,
  value,
  selected,
  onSelect,
  disabled,
}: {
  label: string;
  value: number | null | undefined;
  selected: boolean;
  onSelect: () => void;
  disabled: boolean;
}) => (
  <label
    className={`flex items-center justify-between gap-3 rounded-md border border-gray-200 px-3 py-2 ${
      disabled ? "opacity-60 cursor-not-allowed" : "hover:bg-gray-50 cursor-pointer"
    }`}
  >
    <div className="flex items-center gap-2">
      <input
        type="radio"
        name="priceChoice"
        checked={selected}
        onChange={onSelect}
        disabled={disabled}
      />
      <span className="text-sm text-gray-800">{label}</span>
    </div>
    <span className="text-sm font-medium tabular-nums">{formatGBP(value)}</span>
  </label>
);

export default function ConvertToLeadModal({
  open,
  setOpen,
  quote,
  onConfirm,
  isLoading,
  isSav,
  onAmendPrice,
  onAmendContact,
  onAmendPayment,
}: {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  quote: IQuote;
  onConfirm: (acceptedAs: QuoteAcceptedAs, choice: QuotePriceSelection, assignedCustomerId: string | null) => void;
  isLoading?: boolean;
  isSav: boolean;
  onAmendPrice: (value: number) => Promise<void> | void;
  onAmendContact?: (data: { fullname: string; email: string; phone: string }) => Promise<void> | void;
  onAmendPayment?: (data: { bankAccountName: string; accountNumber: string; sortCode: string }) => Promise<void> | void;
}) {
  const [choice, setChoice] = useState<QuotePriceSelection>("Collection");
  const [acceptedAs, setAcceptedAs] = useState<QuoteAcceptedAs>("Collection");

  useEffect(() => {
    if (!open) return;

    if (quote.status === Status.AcceptedDropOff) setAcceptedAs("DropOff");
    else setAcceptedAs("Collection");
  }, [open, quote.status]);

  const [amendOpen, setAmendOpen] = useState(false);
  const [editContact, setEditContact] = useState(false);
  const [editPayment, setEditPayment] = useState(false);

  const contact = quote.contact;

  const [contactForm, setContactForm] = useState({
    fullname: contact?.fullname ?? "", // or `${firstName} ${lastName}`
    email: contact?.email ?? "",
    phone: contact?.telephone1 ?? "",
  });

  const [paymentForm, setPaymentForm] = useState({
    bankAccountName: quote.bankAccountName ?? "",
    accountNumber: quote.accountNumber ?? "",
    sortCode: quote.sortCode ?? "",
  });

  useEffect(() => {
    if (!open) return;
    setEditContact(false);
    setEditPayment(false);

    setContactForm({
      fullname: contact?.fullname ?? "",
      email: contact?.email ?? "",
      phone: contact?.telephone1 ?? "",
    });

    setPaymentForm({
      bankAccountName: quote.bankAccountName ?? "",
      accountNumber: quote.accountNumber ?? "",
      sortCode: quote.sortCode ?? "",
    });
  }, [open, quote.id]);

  const collectionEnabled = isUsablePrice(quote.priceCollected);
  const dropOffEnabled    = isUsablePrice(quote.priceDropOff);
  const amendedEnabled    = isUsablePrice(quote.amendedPrice);

  // sensible default: match quote status
  useEffect(() => {
    if (!open) return;

    // if current choice is invalid, pick the first valid option
    const validChoices: QuotePriceSelection[] = [];
    if (collectionEnabled) validChoices.push("Collection");
    if (dropOffEnabled) validChoices.push("DropOff");
    if (amendedEnabled) validChoices.push("Amended");

    if (validChoices.length && !validChoices.includes(choice)) {
      setChoice(validChoices[0]);
    }
  }, [open, collectionEnabled, dropOffEnabled, amendedEnabled]);

  const [assignedCustomerId, setAssignedCustomerId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setAssignedCustomerId(quote.assignedCustomerId ?? null);
  }, [open, quote.assignedCustomerId]);

  const isBusy = !!isLoading;

  const defaultAssignedCustomerId =
    quote.assignedCustomerId ??
    quote.sourceCustomerId ??
    quote.collectedCustomerId ??
    quote.dropOffCustomerId ??
    null;

  useEffect(() => {
    if (!open) return;
    setAssignedCustomerId(defaultAssignedCustomerId);
  }, [open, defaultAssignedCustomerId]);

  const saveContact = async () => {
    if (!contact?.id) return;

    await onAmendContact?.({
      fullname: contactForm.fullname,
      email: contactForm.email,
      phone: contactForm.phone,
    });
    setEditContact(false);
  };

  const savePayment = async () => {
    // await onUpdateQuotePayment?.(quote.id, paymentForm); // or mutation
    onAmendPayment?.({
      bankAccountName: paymentForm.bankAccountName,
      accountNumber: paymentForm.accountNumber,
      sortCode: paymentForm.sortCode,
    });
    setEditPayment(false);
  };

  return (
    <Modal width="max-w-4xl" open={open} setOpen={setOpen}>
      <AmendPriceModal
        open={amendOpen}
        setOpen={setAmendOpen}
        initialValue={quote.amendedPrice}
        collectionPrice={quote.priceCollected}
        dropOffPrice={quote.priceDropOff}
        isLoading={isLoading}
        onConfirm={async (value) => {
          await onAmendPrice(value);
          setAmendOpen(false);
          setChoice("Amended"); // nice: auto select amended after editing
        }}
      />

      <div className="space-y-4">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-xl font-semibold text-gray-900">Convert to lead</h1>
            <p className="mt-1 text-sm text-gray-600">
              Choose which price to use. We’ll store your choice as the amended price before converting.
            </p>
            {isSav && !assignedCustomerId && (
              <p className="text-sm text-red-600">
                Please select a customer to assign this quote to.
              </p>
            )}
          </div>

          {isSav && (
            <CustomerPickerPill
              value={assignedCustomerId}
              onChange={(id) => setAssignedCustomerId(id)}
            />
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {(["Collection", "DropOff"] as QuoteAcceptedAs[]).map((opt) => {
            const active = acceptedAs === opt;
            return (
              <button
                key={opt}
                type="button"
                onClick={() => setAcceptedAs(opt)}
                className={
                  "rounded-md border px-3 py-2 text-sm font-medium transition " +
                  (active
                    ? "border-primary-500 bg-primary-50 text-primary-700"
                    : "border-gray-200 bg-white text-gray-700 hover:bg-gray-50")
                }
              >
                {opt === "DropOff" ? "Drop Off" : opt}
              </button>
            );
          })}
        </div>

        {/* ----------------------------------------------- */}
        {/* --------------- VEHICLE DETAILS --------------- */}
        {/* ----------------------------------------------- */}
        <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-gray-500">VRM</span>
              <span className="font-semibold text-gray-900 tabular-nums">
                {quote.vehicleRegistration}
              </span>
            </div>

            <div className="flex items-center justify-between border-l border-gray-200 pl-4">
              <span className="text-gray-500">Postcode</span>
              <span className="font-semibold text-gray-900">
                {quote.postcode}
              </span>
            </div>
          </div>
        </div>


        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* ----------------------------------------------- */}
          {/* --------------- CONTACT DETAILS --------------- */}
          {/* ----------------------------------------------- */}       
          <InfoBox
            title="Contact details"
            editing={editContact}
            onEdit={() => setEditContact(true)}
            onCancel={() => {
              setEditContact(false);
              setContactForm({
                fullname: contact?.fullname ?? "",
                email: contact?.email ?? "",
                phone: contact?.telephone1 ?? "",
              });
            }}
            onSave={saveContact}
          >
            {!editContact ? (
              <>
                <InfoRow label="Name">{contactForm.fullname || "—"}</InfoRow>
                <InfoRow label="Email">{contactForm.email || "—"}</InfoRow>
                <InfoRow label="Phone">{contactForm.phone || "—"}</InfoRow>
              </>
            ) : (
              <div className="space-y-2">
                <div className="flex justify-between gap-4 items-center">
                  <span className="text-gray-600">Name</span>
                  <input
                    className="w-[220px] text-sm border border-gray-300 rounded px-2 py-1 bg-white"
                    value={contactForm.fullname}
                    onChange={(e) => setContactForm((s) => ({ ...s, fullname: e.target.value }))}
                  />
                </div>

                <div className="flex justify-between gap-4 items-center">
                  <span className="text-gray-600">Email</span>
                  <input
                    className="w-[220px] text-sm border border-gray-300 rounded px-2 py-1 bg-white"
                    value={contactForm.email}
                    onChange={(e) => setContactForm((s) => ({ ...s, email: e.target.value }))}
                  />
                </div>

                <div className="flex justify-between gap-4 items-center">
                  <span className="text-gray-600">Phone</span>
                  <input
                    className="w-[220px] text-sm border border-gray-300 rounded px-2 py-1 bg-white"
                    value={contactForm.phone}
                    onChange={(e) => setContactForm((s) => ({ ...s, phone: e.target.value }))}
                  />
                </div>
              </div>
            )}
          </InfoBox>
          {/* ----------------------------------------------- */}
          {/* --------------- PAYMENT DETAILS --------------- */}
          {/* ----------------------------------------------- */}
          <InfoBox
            title="Payment details"
            editing={editPayment}
            onEdit={() => setEditPayment(true)}
            onCancel={() => {
              setEditPayment(false);
              setPaymentForm({
                bankAccountName: quote.bankAccountName ?? "",
                accountNumber: quote.accountNumber ?? "",
                sortCode: quote.sortCode ?? "",
              });
            }}
            onSave={savePayment}
          >
            {!editPayment ? (
              <>
                <InfoRow label="Account name">{paymentForm.bankAccountName || "—"}</InfoRow>
                <InfoRow label="Account number">{paymentForm.accountNumber || "—"}</InfoRow>
                <InfoRow label="Sort code">{paymentForm.sortCode || "—"}</InfoRow>
              </>
            ) : (
              <div className="space-y-2">
                <div className="flex justify-between gap-4 items-center">
                  <span className="text-gray-600">Account name</span>
                  <input
                    className="w-[220px] text-sm border border-gray-300 rounded px-2 py-1 bg-white"
                    value={paymentForm.bankAccountName}
                    onChange={(e) => setPaymentForm((s) => ({ ...s, bankAccountName: e.target.value }))}
                  />
                </div>

                <div className="flex justify-between gap-4 items-center">
                  <span className="text-gray-600">Account number</span>
                  <input
                    className="w-[220px] text-sm border border-gray-300 rounded px-2 py-1 bg-white"
                    value={paymentForm.accountNumber}
                    onChange={(e) => setPaymentForm((s) => ({ ...s, accountNumber: e.target.value }))}
                  />
                </div>

                <div className="flex justify-between gap-4 items-center">
                  <span className="text-gray-600">Sort code</span>
                  <input
                    className="w-[220px] text-sm border border-gray-300 rounded px-2 py-1 bg-white"
                    value={paymentForm.sortCode}
                    onChange={(e) => setPaymentForm((s) => ({ ...s, sortCode: e.target.value }))}
                  />
                </div>
              </div>
            )}
          </InfoBox>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-1 gap-4">
          <div className="rounded-lg border border-gray-200 p-3 space-y-2">
            <div className="text-sm font-semibold text-gray-700">
              Price to use
              <button
                type="button"
                onClick={() => setAmendOpen(true)}
                className="float-right inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:text-primary-800"
              >
                <PencilSquareIcon className="h-4 w-4" />
                Amend price
              </button>
            </div>

            <PriceOptionRow
              label="Collection"
              value={quote.priceCollected}
              selected={choice === "Collection"}
              onSelect={() => setChoice("Collection")}
              disabled={!collectionEnabled}
            />

            <PriceOptionRow
              label="Drop Off"
              value={quote.priceDropOff}
              selected={choice === "DropOff"}
              onSelect={() => setChoice("DropOff")}
              disabled={!dropOffEnabled}
            />

            <PriceOptionRow
              label="Amended"
              value={quote.amendedPrice ?? null}
              selected={choice === "Amended"}
              onSelect={() => setChoice("Amended")}
              disabled={!amendedEnabled}
            />
          </div>
        </div>

        <div className="flex justify-between gap-3 pt-1">
          <ButtonInput
            label={"Cancel"}
            isSubmit={false}
            onClick={() => setOpen(false)}
            disabled={isBusy}
            classes="!border-gray-300 !bg-white !text-gray-700 hover:!bg-gray-100"
          />

          <ButtonInput
            label={isBusy ? "Converting..." : "Convert to lead"}
            isSubmit={false}
            onClick={() => onConfirm(acceptedAs, choice, isSav ? assignedCustomerId : null)}
            disabled={
              isBusy ||
              (choice === "Amended" && !amendedEnabled) || 
              (isSav && !assignedCustomerId) ||
              (!collectionEnabled && choice === "Collection") ||
              (!dropOffEnabled && choice === "DropOff") ||
              (!amendedEnabled && choice === "Amended")
            }
          />
        </div>
      </div>
    </Modal>
  );
}